import { get, has, isNil, isPlainObject, isString, sortBy, toLower, trim } from 'lodash';

import { getConditionalObject } from 'utils/data';
import { naturalSort } from 'utils/sort';

import { PINNED_LABEL_RULES, PinnedPosition } from './AutocompleteCDS.constants';

export const withAriaLabel = (params, ariaLabel) => ({
  ...params,
  inputProps: {
    ...params.inputProps,
    ...getConditionalObject(!!ariaLabel, () => ({ 'aria-label': ariaLabel })),
  },
});

export const getOptionLabel = ({ option, key }) => get(option, key, option?.key ?? '') || '';

export const scrollToSelectedOption = (listbox, selectedOption) => {
  if (!listbox || !selectedOption) {
    return;
  }

  setTimeout(() => {
    listbox.scrollTo({
      top: selectedOption.offsetTop - listbox.clientHeight / 2 + selectedOption.clientHeight / 2,
      behavior: 'auto',
    });
  });
};

const getOptionValue = option => (isPlainObject(option) ? option.value : option);

const hasValueField = option => !isPlainObject(option) || has(option, 'value');

const isEmptyValue = value => isNil(value) || value === '';

const matchesLabelRule = (normalizedLabel, { equals = [], startsWith = [] }) =>
  equals.includes(normalizedLabel) || startsWith.some(prefix => normalizedLabel.startsWith(prefix));

const resolvePinnedSection = (option, { getOptionLabel: getLabel, pinnedOptions }) => {
  if (option?.pinned === PinnedPosition.TOP || option?.pinned === PinnedPosition.BOTTOM) {
    return { section: option.pinned, rank: -1 };
  }

  const value = getOptionValue(option);
  const normalizedLabel = toLower(trim(getLabel(option)));

  const matchesOverride = candidate =>
    candidate === value || (isString(candidate) && toLower(candidate) === normalizedLabel);

  if (pinnedOptions?.top?.some(matchesOverride)) {
    return { section: PinnedPosition.TOP, rank: -1 };
  }

  if (pinnedOptions?.bottom?.some(matchesOverride)) {
    return { section: PinnedPosition.BOTTOM, rank: -1 };
  }

  if (hasValueField(option) && isEmptyValue(value)) {
    return { section: PinnedPosition.TOP, rank: -1 };
  }

  const topRank = PINNED_LABEL_RULES[PinnedPosition.TOP].findIndex(rule =>
    matchesLabelRule(normalizedLabel, rule),
  );

  if (topRank !== -1) {
    return { section: PinnedPosition.TOP, rank: topRank };
  }

  const bottomRank = PINNED_LABEL_RULES[PinnedPosition.BOTTOM].findIndex(rule =>
    matchesLabelRule(normalizedLabel, rule),
  );

  if (bottomRank !== -1) {
    return { section: PinnedPosition.BOTTOM, rank: bottomRank };
  }

  return null;
};

export const sortOptionsWithPins = (
  options,
  { getOptionLabel: getLabel, groupBy, pinnedOptions, isIgnorePins },
) => {
  const sortNaturally = items =>
    [...items].sort(
      (optionA, optionB) =>
        naturalSort(groupBy?.(optionA), groupBy?.(optionB)) ||
        naturalSort(getLabel(optionA), getLabel(optionB)),
    );

  if (isIgnorePins) {
    return sortNaturally(options);
  }

  const pinnedTop = [];
  const middle = [];
  const pinnedBottom = [];

  options.forEach((option, index) => {
    const pin = resolvePinnedSection(option, { getOptionLabel: getLabel, pinnedOptions });

    if (pin?.section === PinnedPosition.TOP) {
      pinnedTop.push({ option, rank: pin.rank, index });

      return;
    }

    if (pin?.section === PinnedPosition.BOTTOM) {
      pinnedBottom.push({ option, rank: pin.rank, index });

      return;
    }

    middle.push(option);
  });

  const sortedMiddle = sortNaturally(middle);

  const orderPinned = pinned => sortBy(pinned, ['rank', 'index']).map(entry => entry.option);

  return [...orderPinned(pinnedTop), ...sortedMiddle, ...orderPinned(pinnedBottom)];
};
